import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { getSharedVideoDownloadUrl } from '../lib/api';

// VIDEO_NOT_READY_STATUS is the HTTP status the backend returns when an item exists but its original video file hasn't
// finished landing in storage yet (a rare upload race).
const VIDEO_NOT_READY_STATUS = 409;

// triggerBrowserDownload navigates an off-screen anchor to url to start a download. The pre-signed URL already carries a
// `Content-Disposition: attachment` response header, so the browser downloads the file rather than navigating to it
// (the `download` attribute is ignored for cross-origin URLs).
const triggerBrowserDownload = (url: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

// useDownloadSharedVideo returns a `downloadVideo(itemId)` function that downloads the original upload of a video in the
// share identified by shortCode, an `isDownloading` flag, and an `errorMessage` describing the last failure (or null).
export const useDownloadSharedVideo = (shortCode: string) => {
  const {
    isPending,
    error,
    mutate: downloadVideo
  } = useMutation({
    mutationFn: (itemId: string) => getSharedVideoDownloadUrl(shortCode, itemId),
    onSuccess: ({ url }) => triggerBrowserDownload(url)
  });

  let errorMessage: string | null = null;
  if (error) {
    errorMessage =
      isAxiosError(error) && error.response?.status === VIDEO_NOT_READY_STATUS
        ? 'This video is still being processed. Please check back in a few minutes.'
        : 'Unable to download the video. Please try again.';
  }

  return { isDownloading: isPending, errorMessage, downloadVideo };
};
